import { useUploadFiles, useUploadToOpenAI } from '@newfront-insurance/admin-ui/files';
import { isNotEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';

// Max file size based on OpenAI file size limit
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

const SUPPORTED_FILE_TYPES = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.xlsx',
  '.xls',
  '.doc',
  '.docx',
  '.eml',
  '.csv',
  '.zip',
  '.pptx',
];

export interface FileAttachment {
  file: File;
  fileUuid?: string;
  isUploading: boolean;
  uploadError?: string;
  tempId: string; // Unique identifier for tracking during upload
}

interface UseFileSelectionOptions {
  setAttachments: Dispatch<SetStateAction<Array<FileAttachment>>>;
  showNotifications?: boolean; // Whether to show error notifications, defaults to true
  enableAiUpload?: boolean; // Whether to trigger eager AI provider upload after commit, defaults to true
  chatUuid?: string; // Chat UUID to associate the file with in the AI provider index
}

export function useFileSelection({
  setAttachments,
  showNotifications = true,
  enableAiUpload = true,
  chatUuid,
}: UseFileSelectionOptions) {
  const { add: addNotification } = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { upload, uploadProgress } = useUploadFiles();
  const { triggerUpload: triggerOpenAIUpload } = useUploadToOpenAI();

  /** Process files from either file input or drag/drop */
  const processFiles = useCallback(
    async (files: FileList) => {
      browserLogger.info(`[Nova] Processing files for upload`, {
        fileCount: files.length,
        fileNames: Array.from(files).map((f) => f.name),
        fileSizes: Array.from(files).map((f) => f.size),
      });

      const unsupportedFileTypes: string[] = [];
      const oversizedFileNames: string[] = [];
      const supportedFiles: File[] = [];
      Array.from(files).forEach((file) => {
        const dotIndex = file.name.lastIndexOf('.');
        const fileExtension = dotIndex !== -1 ? file.name.slice(dotIndex).toLowerCase() : '';
        if (!SUPPORTED_FILE_TYPES.includes(fileExtension)) {
          unsupportedFileTypes.push(fileExtension);
        } else if (file.size > MAX_FILE_SIZE_BYTES) {
          oversizedFileNames.push(file.name);
        } else {
          supportedFiles.push(file);
        }
      });

      if (unsupportedFileTypes.length > 0) {
        browserLogger.warn(`[Nova] Unsupported file types detected`, {
          unsupportedTypes: unsupportedFileTypes,
        });
        if (showNotifications) {
          addNotification({
            duration: 3000,
            title: `The following file types are not supported: ${unsupportedFileTypes.join(', ')}`,
            type: 'error',
          });
        }
      }

      if (oversizedFileNames.length > 0) {
        browserLogger.warn(`[Nova] Files exceed maximum size limit`, {
          fileNames: oversizedFileNames,
          maxSizeMB: MAX_FILE_SIZE_BYTES / (1024 * 1024),
        });
        if (showNotifications) {
          addNotification({
            duration: 5000,
            title: `The following files exceed the 50MB size limit: ${oversizedFileNames.join(', ')}`,
            type: 'error',
          });
        }
      }

      // Process each file independently to avoid race conditions
      const uploadPromises = supportedFiles.map(async (file) => {
        const tempId = `${Date.now()}-${Math.random()}`;

        // Add file to attachments state with uploading status and temp ID
        const newAttachment: FileAttachment = {
          file,
          isUploading: true,
          tempId,
        };

        setAttachments((existingAttachments) => [...existingAttachments, newAttachment]);

        browserLogger.info(`[Nova] Starting individual file upload`, {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          tempId,
        });

        try {
          const response = await upload({
            files: [
              {
                rawFile: file,
                category: 'AI_CHAT_ATTACHMENT',
              },
            ],
          });

          const fileUuid = response[0]?.id;
          browserLogger.info(`[Nova] File upload completed`, {
            fileName: file.name,
            tempId,
            fileUuid,
            success: !!fileUuid,
          });

          if (fileUuid && enableAiUpload) {
            const resolvedChatUuid = chatUuid?.replace('new-chat-', '') || undefined;
            browserLogger.info(`[Nova] Triggering AI provider upload and index`, {
              fileName: file.name,
              tempId,
              fileUuid,
              resolvedChatUuid,
            });
            await triggerOpenAIUpload(fileUuid, resolvedChatUuid);
          }

          setAttachments((prev) =>
            prev.map((attachment) =>
              attachment.tempId === tempId
                ? {
                    ...attachment,
                    fileUuid,
                    isUploading: false,
                    uploadError: fileUuid ? undefined : 'Upload failed',
                  }
                : attachment,
            ),
          );
        } catch (error) {
          browserLogger.error(`[Nova] File upload failed`, {
            fileName: file.name,
            fileSize: file.size,
            tempId,
            error: error instanceof Error ? error.message : 'Unknown error',
            errorStack: error instanceof Error ? error.stack : undefined,
          });

          // Mark this specific upload as failed
          setAttachments((prev) =>
            prev.map((attachment) =>
              attachment.tempId === tempId
                ? {
                    ...attachment,
                    isUploading: false,
                    uploadError: 'Upload failed',
                  }
                : attachment,
            ),
          );
        }
      });

      // Execute all uploads in parallel
      const results = await Promise.allSettled(uploadPromises);

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failureCount = results.filter((r) => r.status === 'rejected').length;

      browserLogger.info(`[Nova] All file uploads completed`, {
        totalFiles: supportedFiles.length,
        successCount,
        failureCount,
      });
    },
    [addNotification, setAttachments, upload, showNotifications, triggerOpenAIUpload, enableAiUpload, chatUuid],
  );

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files) return;

      browserLogger.info(`[Nova] Files selected via file input`, {
        fileCount: files.length,
        fileNames: Array.from(files).map((f) => f.name),
      });

      processFiles(files);

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles],
  );

  /** Handle drag and drop functionality */
  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isDragging) {
        browserLogger.info(`[Nova] User started dragging files over drop zone`);
      }
      setIsDragging(true);
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    browserLogger.info(`[Nova] User dragged files away from drop zone`);
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const { files } = event.dataTransfer;

      browserLogger.info(`[Nova] Files dropped in drop zone`, {
        fileCount: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });

      if (isNotEmpty(files)) {
        processFiles(files);
      } else {
        browserLogger.warn(`[Nova] Drop event contained no files`);
      }
    },
    [processFiles],
  );

  /** Handle clipboard paste functionality */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      // Check if clipboardData exists and has files
      const files = event.clipboardData?.files;

      browserLogger.info(`[Nova] Files pasted from clipboard`, {
        fileCount: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });

      if (isNotEmpty(files)) {
        // Don't prevent default if there are files to process
        event.preventDefault();
        processFiles(files);
      } else {
        browserLogger.debug(`[Nova] Paste event contained no files`);
        // Let the default paste behavior happen for text
      }
    },
    [processFiles],
  );

  /** When the attachment button is clicked, open the native file input */
  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    supportedFileTypes: SUPPORTED_FILE_TYPES,
    uploadProgress,
  };
}
